/**
 * Projection for a merged run whose own record still names an owner-only gate.
 *
 * The stored summary and success flag stay as Brunel wrote them. This only
 * decides the status label and the owner's waiting list. It never applies a
 * migration, rolls out a release, or treats a merge or a published build as live.
 */

export const MERGED_AWAITING_OWNER = 'merged · awaiting owner';

export type OwnerGateLabel = 'hosted migration' | 'node release rollout';

export interface OwnerGateRun {
  readonly assignmentId: string;
  readonly success: boolean;
  readonly summary: string;
  readonly outputTail: string | null;
  readonly reportedAt: string;
}

export interface OwnerGateNodeInput {
  readonly name: string;
  readonly runningVersion: string | null;
  readonly updateState: 'applied' | 'rolled_back' | 'failed' | null;
  readonly updateVersion: string | null;
}

export interface OwnerGateEvidenceInput {
  readonly appliedMigrationTip: string | null;
  /** Published node_releases are accepted and never treated as a running build. */
  readonly publishedNodeReleases?: readonly { readonly version: string }[];
  readonly nodes: readonly OwnerGateNodeInput[];
}

export interface OwnerGateEvidence {
  readonly appliedMigrationVersions: readonly string[];
  readonly nodes: readonly { readonly name: string; readonly versions: readonly string[] }[];
}

export interface OwnerGateRunView {
  readonly assignmentId: string;
  readonly awaitingOwner: boolean;
  readonly statusLabel: typeof MERGED_AWAITING_OWNER | null;
}

export interface OwnerGateWaitingItem {
  readonly id: string;
  readonly assignmentId: string;
  readonly pullRequest: string;
  readonly gate: OwnerGateLabel;
  readonly target: string;
  readonly version: string | null;
  readonly reportedAt: string;
}

export interface OwnerGateProjection {
  readonly views: ReadonlyMap<string, OwnerGateRunView>;
  readonly waiting: readonly OwnerGateWaitingItem[];
}

const PR_NOT_RECORDED = 'PR not recorded';
const ADJACENT = 500;

interface Draft {
  readonly dedup: string;
  readonly gate: OwnerGateLabel;
  readonly target: string;
  readonly version: string | null;
  readonly pullRequest: string;
  readonly assignmentId: string;
  readonly reportedAt: string;
}

function reportedMs(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

function stripMarkup(text: string): string {
  return text.replace(/[*`]/g, '');
}

function normName(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

function gitHex(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim().toLowerCase();
  const prefixed = /^git-([0-9a-f]{12,40})$/.exec(trimmed);
  if (prefixed?.[1]) return prefixed[1];
  const bare = /^[0-9a-f]{12,40}$/.exec(trimmed);
  return bare?.[0] ?? null;
}

function sameGit(left: string, right: string): boolean {
  const a = gitHex(left);
  const b = gitHex(right);
  if (!a || !b) return left.trim().toLowerCase() === right.trim().toLowerCase();
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  return shorter.length >= 12 && longer.startsWith(shorter);
}

function gitLabel(value: string): string | null {
  const hex = gitHex(value);
  return hex === null ? null : `git-${hex}`;
}

function migrationVersions(tip: string | null): readonly string[] {
  if (tip === null) return [];
  const trimmed = tip.trim();
  if (trimmed === '' || trimmed.toLowerCase() === 'unknown') return [];
  return [...new Set(trimmed.match(/(?<!\d)\d{14}(?!\d)/g) ?? [])];
}

/** Recorded ledger only. A later tip does not imply earlier versions were applied. */
export function ownerGateEvidence(input: OwnerGateEvidenceInput): OwnerGateEvidence {
  void input.publishedNodeReleases;
  return {
    appliedMigrationVersions: migrationVersions(input.appliedMigrationTip),
    nodes: input.nodes.map((node) => {
      const versions: string[] = [];
      if (node.runningVersion && node.runningVersion.trim() !== '') versions.push(node.runningVersion);
      if (node.updateState === 'applied' && node.updateVersion && node.updateVersion.trim() !== '') {
        versions.push(node.updateVersion);
      }
      return { name: node.name, versions };
    }),
  };
}

function pullRequestNear(text: string, index: number, length: number): string {
  const start = Math.max(0, index - ADJACENT);
  const end = Math.min(text.length, index + length + ADJACENT);
  const match = /(?:\bPR|\bpull request)\s+#(\d{1,6})\b/i.exec(text.slice(start, end));
  return match?.[1] ? `PR #${match[1]}` : PR_NOT_RECORDED;
}

function structuredPullRequest(value: string): string {
  const match = /#(\d{1,6})/.exec(value);
  return match?.[1] ? `PR #${match[1]}` : PR_NOT_RECORDED;
}

function migrationFromTarget(target: string): { readonly version: string; readonly display: string } | null {
  const path = /supabase\/migrations\/(\d{14})_[a-z0-9_]+\.sql/i.exec(target);
  if (path?.[1]) return { version: path[1], display: path[0] };
  const bare = /(?<!\d)(\d{14})(?!\d)/.exec(target);
  if (bare?.[1]) return { version: bare[1], display: bare[1] };
  return null;
}

function computerName(sentence: string): string | null {
  const re = /(?:\bon|\bto)\s+(?:the\s+)?([A-Za-z0-9][A-Za-z0-9]*(?:[ .'_-][A-Za-z0-9]+){0,4})/gi;
  let name: string | null = null;
  for (let match = re.exec(sentence); match !== null; match = re.exec(sentence)) {
    const candidate = match[1]?.replace(/\s+/g, ' ').trim() ?? '';
    if (candidate.length < 2 || /^(hosted|main|github|the)$/i.test(candidate)) continue;
    name = candidate;
  }
  return name;
}

function nodeDedup(name: string, git: string): string | null {
  const hex = gitHex(git);
  if (hex === null) return null;
  return `node:${normName(name)}:${hex.slice(0, 12)}`;
}

function spansNear(left: number, leftEnd: number, right: number, rightEnd: number): boolean {
  const gap = left > rightEnd ? left - rightEnd : right > leftEnd ? right - leftEnd : 0;
  return gap <= ADJACENT;
}

function sentences(text: string): readonly { readonly text: string; readonly index: number }[] {
  const found: { text: string; index: number }[] = [];
  const re = /[^\n.!?]+/g;
  for (let match = re.exec(text); match !== null; match = re.exec(text)) {
    const value = match[0]?.trim() ?? '';
    if (value !== '') found.push({ text: value, index: match.index });
  }
  return found;
}

const OUTSTANDING_SENTENCE = /not applied|not live|not rolled out|rollout remains|has not been rolled out/i;
const NODE_OUTSTANDING = /not live|not rolled out|rollout remains|has not been rolled out/i;

function readText(text: string): { readonly outstanding: readonly Omit<Draft, 'assignmentId' | 'reportedAt'>[]; readonly resolved: readonly string[] } {
  const outstanding: Omit<Draft, 'assignmentId' | 'reportedAt'>[] = [];
  const resolved: string[] = [];
  const structured = /owner gate:\s*(hosted migration|node release rollout)\s*\|\s*target:\s*([^|]+?)\s*\|\s*(?:version:\s*(git-[0-9a-f]{12,40})\s*\|\s*)?pr:\s*(#\d{1,6}|not recorded)\s*\|\s*(outstanding|resolved)/gi;
  for (let match = structured.exec(text); match !== null; match = structured.exec(text)) {
    const kind = match[1]?.toLowerCase();
    const target = match[2]?.trim() ?? '';
    const gitField = match[3] ?? null;
    const pullRequest = structuredPullRequest(match[4] ?? '');
    const state = match[5]?.toLowerCase();
    if (kind === 'hosted migration') {
      const migration = migrationFromTarget(target);
      if (migration === null) continue;
      const dedup = `migration:${migration.version}`;
      if (state === 'resolved') resolved.push(dedup);
      else outstanding.push({ dedup, gate: 'hosted migration', target: migration.display, version: null, pullRequest });
    } else if (kind === 'node release rollout') {
      const git = gitLabel(gitField ?? target);
      const name = gitField ? target : computerName(target);
      if (git === null || name === null) continue;
      const dedup = nodeDedup(name, git);
      if (dedup === null) continue;
      if (state === 'resolved') resolved.push(dedup);
      else outstanding.push({ dedup, gate: 'node release rollout', target: name, version: git, pullRequest });
    }
  }

  const paths: { version: string; display: string; index: number; length: number }[] = [];
  const pathRe = /supabase\/migrations\/(\d{14})_[a-z0-9_]+\.sql/gi;
  for (let match = pathRe.exec(text); match !== null; match = pathRe.exec(text)) {
    if (match[1]) paths.push({ version: match[1], display: match[0], index: match.index, length: match[0].length });
  }
  const phrases: { index: number; length: number }[] = [];
  const phraseRes = [
    /not applied to hosted/gi,
    /hosted migration is not applied/gi,
    /hosted migration not applied/gi,
    /created in the repo only and not applied/gi,
  ];
  for (const phraseRe of phraseRes) {
    for (let match = phraseRe.exec(text); match !== null; match = phraseRe.exec(text)) {
      phrases.push({ index: match.index, length: match[0].length });
    }
  }
  const uniquePaths = [...new Map(paths.map((path) => [path.version, path])).values()];
  for (const path of uniquePaths) {
    const adjacentPhrase = phrases.find((phrase) => spansNear(path.index, path.index + path.length, phrase.index, phrase.index + phrase.length));
    const onlyPath = uniquePaths.length === 1 && phrases.length > 0;
    if (adjacentPhrase === undefined && !onlyPath) continue;
    const anchor = adjacentPhrase ?? phrases[0] ?? path;
    outstanding.push({
      dedup: `migration:${path.version}`,
      gate: 'hosted migration',
      target: path.display,
      version: null,
      pullRequest: pullRequestNear(text, anchor.index, anchor.length),
    });
  }

  for (const sentence of sentences(text)) {
    if (!/node release/i.test(sentence.text) || !NODE_OUTSTANDING.test(sentence.text)) continue;
    const gitMatch = /git-([0-9a-f]{12,40})/i.exec(sentence.text);
    const name = computerName(sentence.text);
    const git = gitMatch?.[1] ? gitLabel(gitMatch[1]) : null;
    if (git === null || name === null) continue;
    const dedup = nodeDedup(name, git);
    if (dedup === null) continue;
    outstanding.push({
      dedup,
      gate: 'node release rollout',
      target: name,
      version: git,
      pullRequest: pullRequestNear(text, sentence.index, sentence.text.length),
    });
  }

  for (const sentence of sentences(text)) {
    if (OUTSTANDING_SENTENCE.test(sentence.text)) continue;
    const named = /hosted migration\s+(\d{14})(?!\d)/gi;
    if (/\bapplied\b/i.test(sentence.text)) {
      for (let match = named.exec(sentence.text); match !== null; match = named.exec(sentence.text)) {
        if (match[1]) resolved.push(`migration:${match[1]}`);
      }
      if (/applied to hosted/i.test(sentence.text)) {
        const namedHere = sentence.text.match(/(?<!\d)\d{14}(?!\d)/g) ?? [];
        if (namedHere.length > 0) namedHere.forEach((version) => resolved.push(`migration:${version}`));
        else if (uniquePaths.length === 1 && uniquePaths[0]) resolved.push(`migration:${uniquePaths[0].version}`);
      }
    }
    const live = /node release\s+(git-[0-9a-f]{12,40})\s+is live on\s+(?:the\s+)?([A-Za-z0-9][A-Za-z0-9]*(?:[ .'_-][A-Za-z0-9]+){0,4})/i.exec(sentence.text);
    const rolled = /rolled out\s+(git-[0-9a-f]{12,40})\s+on\s+(?:the\s+)?([A-Za-z0-9][A-Za-z0-9]*(?:[ .'_-][A-Za-z0-9]+){0,4})/i.exec(sentence.text);
    const hit = live ?? rolled;
    if (hit?.[1] && hit[2]) {
      const dedup = nodeDedup(hit[2], hit[1]);
      if (dedup !== null) resolved.push(dedup);
    }
  }

  return { outstanding: collapse(outstanding), resolved };
}

function collapse(gates: readonly Omit<Draft, 'assignmentId' | 'reportedAt'>[]): Omit<Draft, 'assignmentId' | 'reportedAt'>[] {
  const byKey = new Map<string, Omit<Draft, 'assignmentId' | 'reportedAt'>>();
  for (const gate of gates) {
    const prev = byKey.get(gate.dedup);
    if (prev === undefined) {
      byKey.set(gate.dedup, gate);
      continue;
    }
    const target = gate.target.includes('supabase/migrations/') && !prev.target.includes('supabase/migrations/')
      ? gate.target
      : prev.target;
    const version = gate.version !== null && (prev.version === null || gate.version.length > prev.version.length)
      ? gate.version
      : prev.version;
    const pullRequest = prev.pullRequest === PR_NOT_RECORDED ? gate.pullRequest : prev.pullRequest;
    byKey.set(gate.dedup, { ...prev, target, version, pullRequest });
  }
  return [...byKey.values()];
}

function evidenceResolves(gate: Draft, evidence: OwnerGateEvidence): boolean {
  if (gate.gate === 'hosted migration') {
    const version = /^migration:(\d{14})$/.exec(gate.dedup)?.[1];
    return version !== undefined && evidence.appliedMigrationVersions.includes(version);
  }
  return evidence.nodes.some((node) => normName(node.name) === normName(gate.target)
    && gate.version !== null
    && node.versions.some((version) => sameGit(version, gate.version ?? '')));
}

function dedupe(open: readonly Draft[]): OwnerGateWaitingItem[] {
  const groups = new Map<string, Draft[]>();
  for (const gate of open) {
    const list = groups.get(gate.dedup) ?? [];
    list.push(gate);
    groups.set(gate.dedup, list);
  }
  const items: OwnerGateWaitingItem[] = [];
  for (const [dedup, list] of groups) {
    const sorted = [...list].sort((left, right) => reportedMs(right.reportedAt) - reportedMs(left.reportedAt));
    const newest = sorted[0];
    if (newest === undefined) continue;
    const recorded = sorted.find((row) => row.pullRequest !== PR_NOT_RECORDED);
    items.push({
      id: dedup,
      assignmentId: newest.assignmentId,
      pullRequest: newest.pullRequest !== PR_NOT_RECORDED ? newest.pullRequest : recorded?.pullRequest ?? PR_NOT_RECORDED,
      gate: newest.gate,
      target: newest.target,
      version: newest.version,
      reportedAt: newest.reportedAt,
    });
  }
  items.sort((left, right) => reportedMs(right.reportedAt) - reportedMs(left.reportedAt) || left.id.localeCompare(right.id));
  return items;
}

export function ownerGateWaitingLine(item: Pick<OwnerGateWaitingItem, 'pullRequest' | 'gate' | 'target' | 'version'>): string {
  if (item.gate === 'node release rollout' && item.version) {
    return `${item.pullRequest} · ${item.gate} · ${item.target} · ${item.version}`;
  }
  return `${item.pullRequest} · ${item.gate} · ${item.target}`;
}

/**
 * One view per run, plus one waiting entry per still-outstanding gate and target.
 * Summary text and the success flag are not rewritten.
 */
export function presentOwnerGateWaiting(
  runs: readonly OwnerGateRun[],
  evidence: OwnerGateEvidence,
): OwnerGateProjection {
  const recorded: Draft[] = [];
  const resolved = new Set<string>();
  for (const run of runs) {
    if (!run.success) continue;
    const text = stripMarkup(`${run.summary}\n${run.outputTail ?? ''}`);
    const read = readText(text);
    for (const key of read.resolved) resolved.add(key);
    for (const gate of read.outstanding) {
      recorded.push({ ...gate, assignmentId: run.assignmentId, reportedAt: run.reportedAt });
    }
  }
  for (const gate of recorded) {
    if (evidenceResolves(gate, evidence)) resolved.add(gate.dedup);
  }
  const open = recorded.filter((gate) => !resolved.has(gate.dedup));
  const awaiting = new Set(open.map((gate) => gate.assignmentId));
  const views = new Map<string, OwnerGateRunView>();
  for (const run of runs) {
    const waiting = run.success && awaiting.has(run.assignmentId);
    views.set(run.assignmentId, {
      assignmentId: run.assignmentId,
      awaitingOwner: waiting,
      statusLabel: waiting ? MERGED_AWAITING_OWNER : null,
    });
  }
  return { views, waiting: dedupe(open) };
}
